import { useState } from "react";
import styled, { useTheme } from "styled-components";
import type { DefaultTheme } from "styled-components";
import {
  IconCheckCircle,
  IconError,
  IconAlert,
  IconHistory,
  IconLock,
  IconEdit,
  IconAttachment,
  IconChat,
  IconClose,
} from "suomifi-icons";
import type { ComponentType } from "react";
import type { KayttajaTapahtuma, Laite, TapahtumaTyyppi } from "../types";
import { formatAikaleima } from "../utils/format";
import { useTranslation } from "react-i18next";

interface Props {
  tapahtumat: KayttajaTapahtuma[];
  laitteet: Laite[];
}

type IconComponent = ComponentType<{ color?: string; ariaLabel?: string }>;

interface TyyppiMeta {
  Icon: IconComponent;
  /** Pääväri ikonille — neutraalit tapahtumat saavat himmeämmän, onnistumiset/epäonnistumiset DS-perheväreistä. */
  color: (theme: DefaultTheme, onnistui?: boolean) => string;
}

const tyyppiMeta: Record<TapahtumaTyyppi, TyyppiMeta> = {
  sovellus_rekisteroity: {
    Icon: IconCheckCircle,
    color: (t) => t.colors.successDark1,
  },
  sovellus_paivittynyt: {
    Icon: IconHistory,
    color: (t) => t.colors.depthDark1,
  },
  sovellus_deaktivoitu: {
    Icon: IconClose,
    color: (t) => t.colors.depthBase,
  },
  pin_vaihdettu: {
    Icon: IconLock,
    color: (t) => t.colors.depthDark1,
  },
  sahkoposti_vaihdettu: {
    Icon: IconEdit,
    color: (t) => t.colors.depthDark1,
  },
  id_proofing_aloitettu: {
    Icon: IconHistory,
    color: (t) => t.colors.depthDark1,
  },
  id_proofing_onnistui: {
    Icon: IconCheckCircle,
    color: (t) => t.colors.successDark1,
  },
  id_proofing_epaonnistui: {
    Icon: IconError,
    color: (t) => t.colors.alertBase,
  },
  asiakirjan_paivitys: {
    Icon: IconError,
    color: (t) => t.colors.alertBase,
  },
  tunnistautuminen_palveluun: {
    Icon: IconCheckCircle,
    color: (t, onnistui) =>
      onnistui === false ? t.colors.alertBase : t.colors.successDark1,
  },
  dokumentti_lisatty: {
    Icon: IconAttachment,
    color: (t) => t.colors.depthDark1,
  },
  tukipyynto_lahetetty: {
    Icon: IconChat,
    color: (t) => t.colors.brandBase,
  },
};

const Empty = styled.p`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  color: ${({ theme }) => theme.colors.depthDark1};
  font-style: italic;
  margin: 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.s};
  padding: ${({ theme }) => theme.spacing.s} 0;

  & + & {
    border-top: 1px solid ${({ theme }) => theme.colors.depthLight2};
  }
`;

const IconWrap = styled.div`
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;

  & > svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Otsikko = styled.div`
  font: ${({ theme }) => theme.typography.bodyText};
  font-size: 0.9375rem;
  color: ${({ theme }) => theme.colors.blackBase};
`;

const Konteksti = styled.div`
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const KontekstiAlert = styled(Konteksti)`
  color: ${({ theme }) => theme.colors.alertBase};
`;

const Aika = styled.div`
  flex-shrink: 0;
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.depthDark1};
  white-space: nowrap;
  margin-top: 2px;
`;

function buildKonteksti(
  tapahtuma: KayttajaTapahtuma,
  laitteet: Laite[],
  t: ReturnType<typeof useTranslation>["t"],
): string | null {
  const parts: string[] = [];
  if (tapahtuma.laiteId) {
    const laite = laitteet.find((l) => l.id === tapahtuma.laiteId);
    if (laite) parts.push(laite.nimi);
  }
  if (tapahtuma.konteksti)
    parts.push(t(`common:tukipyynto.aihe.${t(tapahtuma.konteksti)}`));
  return parts.length > 0 ? parts.join(" · ") : null;
}

/* Tunnistautuminen-tapahtumissa palvelun nimi (palvelu-kenttä) on
   tietoa, jota DFAU ei säilytä — vain tunnistuksen onnistuminen/
   epäonnistuminen tunnetaan. Mock-datassa palvelu on indikaattorina
   olemassa, mutta otsikossa käytetään geneerinen termi "verkkopalveluun".
   Muut tapahtumatyypit käyttävät mock-datan kuvaus-kenttää sellaisenaan. */
function tapahtumaOtsikko(
  tapahtuma: KayttajaTapahtuma,
  t: ReturnType<typeof useTranslation>["t"],
): string {
  if (tapahtuma.tyyppi !== "tunnistautuminen_palveluun") {
    return t(`tapahtumat.${tapahtuma.tyyppi}`);
  }
  const onnistuiSuffix =
    tapahtuma.onnistui === false ? "epäonnistui" : "onnistui";
  return `Tunnistautuminen verkkopalveluun ${onnistuiSuffix}`;
}

const NaytaLisaaButton = styled.button`
  background: transparent;
  border: 0;
  padding: ${({ theme }) => theme.spacing.s} 0;
  cursor: pointer;
  font: ${({ theme }) => theme.typography.bodySemiBoldSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.highlightBase};
  text-align: left;
  align-self: flex-start;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.highlightDark1};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.highlightBase};
    outline-offset: 2px;
  }
`;

// Oletuksena näytetään tämän verran tapahtumia, loput "Näytä lisää" -takaa
// (workshop D12: rajoitettu listaus, jotta sektio ei venytä näkymää).
const TAPAHTUMIA_OLETUKSENA = 8;

export default function Tapahtumat({ tapahtumat, laitteet }: Props) {
  const { t, i18n } = useTranslation(["kayttajaKortti", "common"]);
  const theme = useTheme();
  const [naytaKaikki, setNaytaKaikki] = useState(false);

  if (tapahtumat.length === 0) {
    return <Empty>Ei tapahtumia.</Empty>;
  }

  // Kronologinen järjestys: uusin ylhäällä.
  const sorted = [...tapahtumat].sort(
    (a, b) => new Date(b.aika).getTime() - new Date(a.aika).getTime(),
  );

  const naytettavat = naytaKaikki
    ? sorted
    : sorted.slice(0, TAPAHTUMIA_OLETUKSENA);
  const piilossa = sorted.length - naytettavat.length;

  return (
    <List>
      {naytettavat.map((tapahtuma) => {
        const meta = tyyppiMeta[tapahtuma.tyyppi] ?? {
          Icon: IconAlert,
          color: (theme: DefaultTheme) => theme.colors.depthDark1,
        };
        const Icon = meta.Icon;
        const color = meta.color(theme, tapahtuma.onnistui);
        const konteksti = buildKonteksti(tapahtuma, laitteet, t);
        const onError = tapahtuma.onnistui === false;
        return (
          <Row key={tapahtuma.id}>
            <IconWrap>
              <Icon color={color} ariaLabel="" />
            </IconWrap>
            <Body>
              <Otsikko>{tapahtumaOtsikko(tapahtuma, t)}</Otsikko>
              {konteksti &&
                (onError ? (
                  <KontekstiAlert>{konteksti}</KontekstiAlert>
                ) : (
                  <Konteksti>{konteksti}</Konteksti>
                ))}
            </Body>
            <Aika>{formatAikaleima(tapahtuma.aika)}</Aika>
          </Row>
        );
      })}
      {piilossa > 0 && !naytaKaikki && (
        <NaytaLisaaButton type="button" onClick={() => setNaytaKaikki(true)}>
          Näytä loput {piilossa} tapahtumaa
        </NaytaLisaaButton>
      )}
    </List>
  );
}
